import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';

import { CompanyParametersQuery, getAllowedDocumentKindsForNumbering, NumberingBO, NumberingUtils, withModule } from '@skylab-shared/business';
import { ValueConverter } from '@skylab-shared/core';
import { ConfigurationDocumentKind, GapFillingMethod, InventoryDocumentKind, LabeledEnumValue, LabeledEnumValueConverter, NumberingModel, PurchaseDocumentKind, SaleDocumentKind } from '@skylab-shared/dataModel';
import { head } from '@skylab-shared/utils';

import { NumberingRepository, NumberingRepositoryBase } from '@skylab-angular-shared/business';
import { CommandBus, EnumTranslator, EnumTranslatorBase, EventBus, EventBusBase, L10n, L10nBase, RootContext, RootContextBase, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { BooleanValueAccessorDirective, ButtonComponent, DialogContentComponentIns, DialogHeaderComponentIns, DialogInsService, FocusByDirective, FormContainerComponent, HelpInPlaceComponent, HelpInPlaceData, NumericValueAccessorDirective, SelectValueAccessorDirective, TextValueAccessorDirective, ToastService, ValidateDirective, ValidationSourceDirective, IconComponent } from '@skylab-angular-shared/ui';

import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { NumberingsFormatsPopupService } from '../../hintbox/numberings-formats/numberings-formats.service';
import { DictionaryDialogComponentBase, DictionaryDialogViewModelBase } from '../dictionary-dialog-base';

export interface NumberingsDialogResult {
    id: number;
    version: string;
}

interface NumberingsViewModel extends DictionaryDialogViewModelBase<NumberingBO> {
    additionalSymbol: string;
    documentKinds: LabeledEnumValue[];
    documentKindsConverter: ValueConverter<ConfigurationDocumentKind, LabeledEnumValue>;
    documentKind: {
        value: ConfigurationDocumentKind
    };
    formatType: { label: string, value: NumberingUtils.NumerationFormat } | null;
    formatTypes: { label: string, format: string | null; value: NumberingUtils.NumerationFormat }[];
    gapFillingMethods: LabeledEnumValue[];
    gapFillingMethodsConverter: LabeledEnumValueConverter<GapFillingMethod>;
    isAdditionalSymbolShown(): boolean;
    onAdditionalSymbolChange(): void;
    isFormatReadonly(): boolean;
    isFormatTypeReadonly(): boolean;
    onFormatTypeChange(): void;
    getNumberingAutotextsHintobxData: HelpInPlaceData;
}

@Component({
    selector: 'skl-numberings-dialog',
    templateUrl: './numberings-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, FormContainerComponent, ValidationSourceDirective, BooleanValueAccessorDirective, FormsModule, ValidateDirective, TextValueAccessorDirective, FocusByDirective, SelectValueAccessorDirective, NgClass, HelpInPlaceComponent, NumericValueAccessorDirective, ButtonComponent, TranslatePipe, IconComponent]
})
export class NumberingsDialogComponent extends DictionaryDialogComponentBase<
    NumberingModel,
    NumberingBO,
    NumberingRepositoryBase,
    NumberingsViewModel
> {
    public constructor(
        @Inject(NumberingRepository) numberingRepository: NumberingRepositoryBase,
        @Inject(L10n) l10n: L10nBase,
        @Inject(Weaver) weaver: WeaverBase,
        @Inject(RootContext) rootContext: RootContextBase,
        @Inject(EventBus) eventBus: EventBusBase,
        @Inject(EnumTranslator) private readonly enumTranslator: EnumTranslatorBase,
        toastService: ToastService,
        commandBus: CommandBus,
        dialogService: DialogInsService,
        private readonly numberingsFormatsPopupService: NumberingsFormatsPopupService
    ) {
        super(
            numberingRepository,
            toastService,
            l10n,
            weaver,
            rootContext,
            commandBus,
            eventBus,
            dialogService
        );
    }

    protected override async preloadDataAsync(): Promise<void> {
        await this.localContext.preload(CompanyParametersQuery);
    }

    protected override getInitialState(): NumberingsViewModel {
        const self = this;
        const companyParameters = this.localContext.getPreloaded(CompanyParametersQuery!);
        const vatPayer = companyParameters?.vatPayer;

        return {
            ...super.getInitialState(),
            documentKindsConverter: {
                convert(value: ConfigurationDocumentKind) {
                    if (value in SaleDocumentKind) {
                        return self.enumTranslator.getLabeledEnumValue(SaleDocumentKind, value);
                    } else if (value in PurchaseDocumentKind) {
                        return self.enumTranslator.getLabeledEnumValue(PurchaseDocumentKind, value);
                    }
                    return self.enumTranslator.getLabeledEnumValue(InventoryDocumentKind, value);
                },
                convertBack(item: LabeledEnumValue) {
                    return <ConfigurationDocumentKind>item.value;
                }
            },
            documentKinds: getAllowedDocumentKindsForNumbering(self.enumTranslator, {vatPayer: !!vatPayer, inventory: withModule('Inventory')}),
            documentKind: {
                get value() {
                    const kinds = self.viewModel.bo.model.kinds;
                    if (kinds.indexOf(SaleDocumentKind.Invoice) > -1)
                        return SaleDocumentKind.Invoice;
                    if (kinds.indexOf(SaleDocumentKind.InvoiceAdjustment) > -1)
                        return SaleDocumentKind.InvoiceAdjustment;
                    return self.viewModel.bo.model.kinds[0];
                },
                set value(val) {
                    if (val === SaleDocumentKind.Invoice)
                        self.viewModel.bo.model.kinds = [SaleDocumentKind.Invoice, SaleDocumentKind.AdvanceInvoice];
                    else if (val === SaleDocumentKind.InvoiceAdjustment)
                        self.viewModel.bo.model.kinds = [SaleDocumentKind.InvoiceAdjustment, SaleDocumentKind.AdvanceInvoiceAdjustment];
                    else
                        self.viewModel.bo.model.kinds = [val];

                    self.updateFormats();
                    self.viewModel.bo.model.manual = false;
                    self.updateFormatTemplate(NumberingUtils.NumerationFormat.Yearly);
                    if (self.viewModel.additionalSymbol) {
                        self.viewModel.bo.model.format = NumberingUtils.updateFormatSymbol(self.viewModel.additionalSymbol, self.viewModel.bo.model.format)!;
                        self.updateFormats();
                    }
                    self.updateFormatType();
                }
            },
            formatType: null,
            formatTypes: [],
            gapFillingMethods: this.enumTranslator.getLabeledEnumValueArray(GapFillingMethod),
            gapFillingMethodsConverter: new LabeledEnumValueConverter(this.enumTranslator, GapFillingMethod),
            additionalSymbol: '',
            isFormatTypeReadonly: () => this.viewModel.bo.model.documentNumberInUse,
            isFormatReadonly: () => this.viewModel.formatType?.value !== NumberingUtils.NumerationFormat.Custom,
            isAdditionalSymbolShown: () => this.viewModel.formatType ? ![NumberingUtils.NumerationFormat.Custom, NumberingUtils.NumerationFormat.Manual].includes(this.viewModel.formatType.value) : false,
            getNumberingAutotextsHintobxData: ({
                hintbox: {
                    titleText: this.l10n._T('scenes.configuration.shared.numberings.hintbox.formats.title'),
                    content: `
                        ${ this.numberingsFormatsPopupService.getFormattedContent() }
                        <p class="h-text--description">
                            ${this.l10n._T('scenes.configuration.shared.numberings.hintbox.formats.content.format-date-info')}
                        </p>
                    `,
                    cssStyle: { ['--ins-max-width']: '450px' }
                },
                tooltip: this.l10n._T('scenes.configuration.shared.numberings.hintbox.formats.tooltip')
            }),
            onFormatTypeChange: () => {
                if (!this.viewModel.formatType) return;
                this.viewModel.bo.model.manual = this.viewModel.formatType.value === NumberingUtils.NumerationFormat.Manual;

                if (this.viewModel.formatType.value !== NumberingUtils.NumerationFormat.Custom) {
                    this.updateFormatTemplate(this.viewModel.formatType.value);
                }
            },
            onAdditionalSymbolChange: () => {
                this.viewModel.bo.model.format = NumberingUtils.updateFormatSymbol(this.viewModel.additionalSymbol, this.viewModel.bo.model.format)!;
                this.updateFormats();
                this.updateFormatType();
            }
        };
    }

    protected override async prepareViewModelAsync(): Promise<void> {
        await super.prepareViewModelAsync();

        if (!this.data.id) {
            const model = this.viewModel.bo.model;
            model.startingNumber = 1;
            model.gapFillingMethod = GapFillingMethod.None;
            model.active = true;

            this.viewModel.documentKind.value = SaleDocumentKind.Invoice;

            this.updateFormats();
            this.updateFormatTemplate(NumberingUtils.NumerationFormat.Yearly);
        }

        this.viewModel.additionalSymbol = NumberingUtils.getFormatSymbol(this.viewModel.bo.model.format);

        this.updateFormats();
        this.updateFormatType();
    }

    private updateFormatTemplate(newType: NumberingUtils.NumerationFormat): void {
        const chosenType = head(this.viewModel.formatTypes.filter(f => f.value === newType));
        if (chosenType != null) {
            this.viewModel.bo.model.format = chosenType.format || '';
        }
    }

    private updateFormats(): void {
        const formatCore = NumberingUtils.getFormatCore(this.viewModel.bo.model.format, this.viewModel.bo.model.kinds);
        const formatTypes = [
            {
                label: `${this.l10n._T('scenes.configuration.shared.numberings.dialog.form.kind.labels.yearly')} - ${formatCore}${NumberingUtils.tags.yearOrder}`,
                format: `${formatCore}${NumberingUtils.tags.yearOrder}`,
                value: NumberingUtils.NumerationFormat.Yearly
            },
            {
                label: `${this.l10n._T('scenes.configuration.shared.numberings.dialog.form.kind.labels.yearly')} - ${formatCore}${NumberingUtils.tags.month}/${NumberingUtils.tags.yearOrder}`,
                format: `${formatCore}${NumberingUtils.tags.month}/${NumberingUtils.tags.yearOrder}`,
                value: NumberingUtils.NumerationFormat.YearlyWithMonth
            },
            {
                label: `${this.l10n._T('scenes.configuration.shared.numberings.dialog.form.kind.labels.monthly')} - ${formatCore}${NumberingUtils.tags.monthOrder}/${NumberingUtils.tags.yearOrder}`,
                format: `${formatCore}${NumberingUtils.tags.monthOrder}/${NumberingUtils.tags.yearOrder}`,
                value: NumberingUtils.NumerationFormat.Monthly
            },
            {
                label: this.l10n._T('scenes.configuration.shared.numberings.dialog.form.kind.labels.custom'),
                format: null,
                value: NumberingUtils.NumerationFormat.Custom
            }
        ];

        if (this.viewModel.documentKind.value !== PurchaseDocumentKind.EPurchaseInvoice){
            formatTypes.push({
                label: this.l10n._T('scenes.configuration.shared.numberings.dialog.form.kind.labels.manual'),
                format: null,
                value: NumberingUtils.NumerationFormat.Manual
            });
        }
        this.viewModel.formatTypes = formatTypes;
    }

    private updateFormatType(): void {
        if (this.viewModel.bo.model.manual) {
            this.viewModel.formatType = head(this.viewModel.formatTypes.filter(f => f.value === NumberingUtils.NumerationFormat.Manual)) || null;
        } else {
            const formatType = NumberingUtils.getFormatType(this.viewModel.bo.model.format, this.viewModel.bo.model.kinds);
            this.viewModel.formatType = this.viewModel.formatTypes.filter(f => f.value === formatType).shift() || null;
        }
    }
}
